import React, { useState } from 'react';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { formatRelative } from '@/lib/utils';
import { FleetApiError, FullVideoView, fullVideoDownloadUrl } from '@/services/fleet';

function formatVideoSize(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  return `${Math.round(bytes / 1e6)} MB`;
}

const FullVideoButton: React.FC<{
  view: FullVideoView | undefined;
  eventId: number;
  stageIndex: number;
  deviceId: string;
  onPrepare: () => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ view, eventId, stageIndex, deviceId, onPrepare, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!view || view.state === 'empty') return null;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(
        err instanceof FleetApiError && typeof err.detail === 'string'
          ? err.detail
          : err instanceof Error
            ? err.message
            : 'failed',
      );
    } finally {
      setBusy(false);
    }
  };

  const downloadLink = (dl: NonNullable<FullVideoView['download']>, label: string) => {
    const multi = dl.cameras.length > 1;
    return (
      <div className="mt-1">
        <div className="flex flex-wrap items-center gap-2">
          {(multi ? dl.cameras : dl.cameras.slice(0, 1)).map((c) => (
            <Button asChild key={c.camera_id} size="sm" variant="ghost" className="h-7 gap-1.5 text-[11px]">
              <a href={fullVideoDownloadUrl(eventId, stageIndex, deviceId, c.camera_id)}>
                <Download className="size-3" />
                {multi ? (
                  <span>
                    {c.camera_id} · {formatVideoSize(c.size_bytes)}
                  </span>
                ) : (
                  <span>
                    {label}
                    <span className="text-fg-muted">
                      {' '}
                      · {dl.session_count} session{dl.session_count === 1 ? '' : 's'} ·{' '}
                      {formatVideoSize(c.size_bytes)}
                    </span>
                  </span>
                )}
              </a>
            </Button>
          ))}
        </div>
        {dl.notes.length > 0 && (
          <p className="mt-0.5 text-[11px] text-fg-muted">{dl.notes.join(' · ')}</p>
        )}
      </div>
    );
  };

  let body: React.ReactNode = null;

  if (view.state === 'waiting') {
    const reasons = view.blocked_reasons.map((r) =>
      r.includes('recording') ? `${r} — end it under Sessions` : r,
    );
    body = (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled>
            Prepare full-length video
          </Button>
          {view.download && downloadLink(view.download, 'Download full video')}
        </div>
        {reasons.length > 0 && (
          <p className="mt-0.5 text-[11px] text-fg-muted">{reasons.join(' · ')}</p>
        )}
      </>
    );
  } else if (view.state === 'idle') {
    body = (
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-[11px]"
        disabled={busy}
        onClick={() => void run(onPrepare)}
      >
        {busy && <Spinner className="size-3" />}
        Prepare full-length video
      </Button>
    );
  } else if (view.state === 'queued' || view.state === 'running') {
    const job = view.job;
    const phaseText =
      view.state === 'queued'
        ? 'Queued'
        : (job?.phase ??
          (job?.parts_total
            ? `Preparing · session ${(job.parts_done ?? 0) + 1} of ${job.parts_total}`
            : 'Preparing'));
    const cancelling = job?.cancel_requested ?? false;
    body = (
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-fg-secondary">
            <Spinner className="size-3" />
            {phaseText}
          </span>
          {job?.requested_by && (
            <span className="text-[11px] text-fg-muted">
              Started by {job.requested_by} · {formatRelative(job.requested_at)}
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[11px]"
            disabled={busy || cancelling}
            onClick={() => void run(onCancel)}
          >
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        </div>
        {view.download && downloadLink(view.download, 'Download full video')}
      </div>
    );
  } else if (view.state === 'ready' && view.download) {
    body = downloadLink(view.download, 'Download full video');
  } else if (view.state === 'outdated' && view.download) {
    body = (
      <div className="flex flex-wrap items-center gap-2">
        {downloadLink(
          view.download,
          `Download full video (${view.download.session_count} of ${view.session_count} sessions)`,
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px]"
          disabled={busy}
          onClick={() => void run(onPrepare)}
        >
          {busy && <Spinner className="size-3" />}
          Prepare again
        </Button>
      </div>
    );
  } else if (view.state === 'failed') {
    body = (
      <div>
        <p className="text-[11px] text-status-error">Failed: {view.job?.error}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px]"
            disabled={busy}
            onClick={() => void run(onPrepare)}
          >
            {busy && <Spinner className="size-3" />}
            Retry
          </Button>
        </div>
        {view.download && downloadLink(view.download, 'Download full video')}
      </div>
    );
  }

  return (
    <div className="pl-0">
      {body}
      {error && <p className="mt-0.5 text-[11px] text-status-error">{error}</p>}
    </div>
  );
};

export default FullVideoButton;
